import { cloneElement, memo } from "react";

import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { Handle, Position } from "react-flow-renderer";

/**
 * FP 플로우차트 노드(도형) 렌더러 — old `wingui-core/component/workflow/widgets/FCFLOWidget.jsx` 이식.
 *
 * old는 11종 도형을 가졌지만 여기엔 FLO 화면이 실제로 만드는 5종만 옮긴다
 * (나머지는 그 도형을 쓰는 화면 — BomDiagram/ByproductDiagram/OrderTrackingDiagram 등 — 을
 * 이관할 때 SHAPES에 추가한다. "필요 시점에 최소 범위로 만든다" 원칙, 10-FORWARD-PLAN.md §6-3).
 *
 * 도형은 old와 동일한 순수 SVG(`rect`/`ellipse`/`path`)이고 색상도 old 상수를 그대로 쓴다.
 * 툴팁만 old의 SVG `<title>` → MUI `Tooltip`으로 바꿨다(new 관례).
 */
const DEFAULT_SIZE = 40;

// old FCFLOWidget의 problemStatus 강조색(흰 배경 + 굵은 빨간 테두리). 주문 추적의 납기 지연
// 작업주문이 이걸 쓴다.
const PROBLEM_PAINT = { fill: "rgb(255,255,255)", stroke: "rgb(255,0,0)", strokeWidth: 3 };

// old FCFLOWidget의 도형별 기본 색상 + path. viewBox는 old 값을 그대로 유지한다.
// TODO(MIG-T2): [flo/Flo] 도형 5종(location/customer/transport/inventory/inventoryWarehousing) 미이관.
//   old: FCFLOWidget.jsx:352-369이 type별로 11종 SVG 도형을 렌더.
//   사유: 이관된 화면(FLO/BOM/주문추적)이 만드는 도형은 아래 6종뿐 — 나머지는 그 도형을 쓰는
//        화면을 이관할 때 여기 추가한다.
const SHAPES = {
  // 공정
  route: {
    fill: "rgb(251,220,225)", stroke: "rgb(209,26,53)", labelPos: "bottom",
    render: (w, h) => <rect x="0" y="0" width={w} height={h} />
  },
  // 품목(인벤토리)
  item: {
    fill: "rgb(212,234,243)", stroke: "rgb(39,111,139)", labelPos: "bottom",
    viewBox: "0 0 100 70",
    render: () => <path d="M0,70 L50,0 L100,70 z" />
  },
  // 설비(자원)
  resource: {
    fill: "rgb(247,247,171)", stroke: "rgb(210,193,70)", labelPos: "center",
    render: (w, h) => <ellipse cx={w / 2} cy={h / 2} rx={w / 2 - 1} ry={h / 2 - 1} />
  },
  // BOR 세트
  borset: {
    fill: "rgb(227,242,237)", stroke: "rgb(49,104,87)", labelPos: "center",
    viewBox: "0 0 100 100",
    render: () => <path d="M0,50 L50,0 L100,50 L50,100 z" />
  },
  // 공구(툴)
  tool: {
    fill: "rgb(255,204,254)", stroke: "rgb(255,107,238)", labelPos: "center",
    render: (w, h) => <path d={pentagonPath(w, h)} />
  },
  // 주문(작업주문) — old DocumentShape(아래가 물결로 파인 문서 모양)
  order: {
    fill: "rgb(212,212,212)", stroke: "rgb(50,50,50)", labelPos: "bottom",
    viewBox: "0 0 102 61",
    render: () => <path d="M 96 1 C 98.76 1 101 3.24 101 6 L 101 53.5 Q 76 40 51 53.5 Q 26 67 1 53.5 L 1 8.5 L 1 6 C 1 3.24 3.24 1 6 1 Z" />
  }
};

// old Pentagon — 정오각형 꼭짓점을 계산해 path로 그린다.
function pentagonPath(width, height) {
  const cx = width / 2;
  const cy = height / 2;
  const r = Math.min(width, height) / 2;
  const points = [];
  for (let i = 0; i < 5; i += 1) {
    const angle = (i * 2 * Math.PI) / 5 - Math.PI / 2;
    points.push(`${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`);
  }
  return `M${points[0]} L${points[1]} L${points[2]} L${points[3]} L${points[4]} z`;
}

const labelSx = (labelPos) => (labelPos === "center"
  ? { top: "50%", left: "50%", transform: "translate(-50%, -50%)" }
  : { top: "100%", left: "50%", transform: "translateX(-50%)" });

const FlowNode = ({ id, data }) => {
  const shape = SHAPES[data.shape];
  if (!shape) return null;

  const width = data.width ?? DEFAULT_SIZE;
  const height = data.height ?? DEFAULT_SIZE;
  // problemStatus가 있으면 노드 고유색(재고 회색 등)보다 우선해서 경고색으로 덮어쓴다(old와 동일).
  const problem = !!data.problemStatus;
  const fill = problem ? PROBLEM_PAINT.fill : (data.fill ?? shape.fill);
  const stroke = problem ? PROBLEM_PAINT.stroke : (data.stroke ?? shape.stroke);
  const strokeWidth = problem ? PROBLEM_PAINT.strokeWidth : 1;

  const svg = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox={shape.viewBox}
      preserveAspectRatio="none"
      style={{ display: "block", overflow: "visible" }}
    >
      {/* 도형별 element에 공통 색상만 주입 — old와 동일하게 fill/stroke는 도형이 아니라 타입이 정한다. */}
      {cloneElement(shape.render(width, height), { fill, stroke, strokeWidth })}
    </svg>
  );

  return (
    <Box sx={{ position: "relative", width, height }}>
      {/* 엣지 방향은 LR 고정(old Flo.jsx:39 direction="LR") — 자식(왼쪽) → 부모(오른쪽). */}
      <Handle type="source" position={Position.Right} id={`${id}_right`} style={{ opacity: 0 }} isConnectable={false} />
      <Handle type="target" position={Position.Left} id={`${id}_left`} style={{ opacity: 0 }} isConnectable={false} />
      {data.tooltip
        ? <Tooltip title={<Box sx={{ whiteSpace: "pre-line" }}>{data.tooltip}</Box>} placement="top" arrow>{svg}</Tooltip>
        : svg}
      <Box
        sx={{
          position: "absolute", ...labelSx(shape.labelPos),
          fontSize: 11, whiteSpace: "nowrap", pointerEvents: "none", fontWeight: 500
        }}
      >
        {data.label}
      </Box>
    </Box>
  );
};

export default memo(FlowNode);
